import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface DeviceHealth {
  id: string;
  userId: string | null;
  label: string;
  type: string;
  lastActiveAt: string | null;
  revokedAt: string | null;
  hasFcm: boolean;
  ageSeconds: number | null;
  status: 'ACTIVE' | 'DELAYED' | 'OFFLINE' | 'REVOKED' | 'AIRPLANE' | 'NO_SIGNAL' | 'LOW_BATTERY' | 'ON_CALL' | 'SILENT';
  reason: string | null;
  batteryPct: number | null;
  batteryCharging: boolean | null;
  airplaneMode: boolean | null;
  networkType: string | null;
  signalDbm: number | null;
  phoneCallState: string | null;
  ringerMode: string | null;
}

interface UserRow {
  id: string;
  name: string;
  role: string;
  status?: string;
}

// Mobile palette echoed here: green / amber / red / grey at full
// saturation against a white card background so the dots actually
// read at a glance. Old strip was grey-on-grey and unreadable.
const dotClass: Record<string, string> = {
  ACTIVE: 'bg-[#34C759]',
  DELAYED: 'bg-[#FF9500]',
  OFFLINE: 'bg-[#FF3B30]',
  REVOKED: 'bg-zinc-400',
  AIRPLANE: 'bg-[#FF3B30]',
  NO_SIGNAL: 'bg-[#FF3B30]',
  LOW_BATTERY: 'bg-[#FF9500]',
  ON_CALL: 'bg-[#0A84FF]',
  SILENT: 'bg-[#AF52DE]',
};
const chipBg: Record<string, string> = {
  ACTIVE: 'bg-[#E8F8EC] text-[#0D6B2D] border-[#B7E5C2]',
  DELAYED: 'bg-[#FFF4E1] text-[#8A4B00] border-[#FFD9A0]',
  OFFLINE: 'bg-[#FFE9E7] text-[#8C1F19] border-[#FFC4BF]',
  REVOKED: 'bg-zinc-100 text-zinc-600 border-zinc-200',
  AIRPLANE: 'bg-[#FFE9E7] text-[#8C1F19] border-[#FFC4BF]',
  NO_SIGNAL: 'bg-[#FFE9E7] text-[#8C1F19] border-[#FFC4BF]',
  LOW_BATTERY: 'bg-[#FFF4E1] text-[#8A4B00] border-[#FFD9A0]',
  ON_CALL: 'bg-[#E4F0FF] text-[#0A4FA1] border-[#A8C9F2]',
  SILENT: 'bg-[#F4E8FF] text-[#5B2E91] border-[#D9BFF4]',
};

// User-facing labels, per the operator's spec: collapse heartbeat-noise
// states to "active" and only call something out when it's genuinely
// failing (switched off, in flight mode, no signal, dying battery).
const labelText: Record<string, string> = {
  ACTIVE: 'active',
  DELAYED: 'active', // heartbeat blip — treat as healthy in the chip
  OFFLINE: 'switched off',
  REVOKED: 'revoked',
  AIRPLANE: '✈ flight mode',
  NO_SIGNAL: 'no signal',
  LOW_BATTERY: 'low battery',
  ON_CALL: '📞 on call',
  SILENT: '🔇 silent',
};

/**
 * Compact strip across the top of the admin dashboard showing the
 * health of every relay phone. The single most important pre-payment
 * signal — if Shubham's phone is RED, the operator knows NOT to start
 * a transaction on Shubham's card. Polls every 30s; pulls from
 * `/api/devices/health` which the backend updates on every heartbeat.
 */
export function RelayHealthStrip() {
  const { data: health } = useQuery({
    queryKey: ['devices', 'health'],
    queryFn: async () => api<DeviceHealth[]>('/api/devices/health'),
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: async () => api<UserRow[]>('/api/users'),
    staleTime: 5 * 60_000,
  });

  if (!health || health.length === 0) return null;

  // Group by user — collapse multiple devices for the same user to the
  // one with the most recent heartbeat. Picking by `lastActiveAt` (not
  // by status) avoids the bug where a stale OFFLINE device with rank 5
  // was hiding the same user's live ACTIVE one because the rank
  // comparison had it backwards.
  const byUser = new Map<string, DeviceHealth>();
  for (const d of health) {
    if (!d.userId) continue;
    if (d.status === 'REVOKED') continue; // de-registered phones never belong here
    const prev = byUser.get(d.userId);
    const dStamp = d.lastActiveAt ? new Date(d.lastActiveAt).getTime() : 0;
    const prevStamp = prev?.lastActiveAt ? new Date(prev.lastActiveAt).getTime() : 0;
    if (!prev || dStamp > prevStamp) byUser.set(d.userId, d);
  }
  const userById = new Map((users ?? []).map((u) => [u.id, u]));

  const rows = Array.from(byUser.entries())
    .map(([uid, d]) => ({ user: userById.get(uid), d }))
    .filter((r): r is { user: UserRow; d: DeviceHealth } =>
      Boolean(r.user) && (!r.user!.status || r.user!.status === 'ACTIVE'))
    .sort((a, b) => rank(a.d.status) - rank(b.d.status));

  if (rows.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-zinc-200 bg-white px-4 py-3 shadow-sm">
      <span className="mr-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-zinc-500">
        Relay phones
      </span>
      {rows.map(({ user, d }) => (
        <span
          key={d.id}
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${chipBg[d.status] ?? 'bg-zinc-100 text-zinc-700 border-zinc-200'}`}
          title={d.reason ?? `${user.name} · ${d.status}`}
        >
          <span className={`size-2 rounded-full ${dotClass[d.status] ?? 'bg-zinc-500'}`} />
          <span className="font-semibold">{user.name}</span>
          <span className="opacity-80">
            · {labelText[d.status] ?? d.status.toLowerCase()}
            {d.batteryPct != null && (d.status === 'LOW_BATTERY' || d.status === 'ACTIVE')
              ? ` · 🔋${d.batteryPct}%${d.batteryCharging ? '⚡' : ''}`
              : ''}
            {d.ageSeconds != null && d.status !== 'ACTIVE'
              ? ` · ${formatAge(d.ageSeconds)}`
              : ''}
          </span>
        </span>
      ))}
    </div>
  );
}

function rank(s: DeviceHealth['status']): number {
  // Ordering for "best device wins" when a user has multiple registered
  // phones. Lower = healthier.
  return {
    ACTIVE: 0,
    SILENT: 1,        // SMS still arrives, just no audible alert — still healthy
    ON_CALL: 2,       // can receive SMS but admin is busy
    LOW_BATTERY: 3,
    DELAYED: 4,
    NO_SIGNAL: 5,
    AIRPLANE: 6,
    OFFLINE: 7,
    REVOKED: 8,
  }[s] ?? 7;
}

function formatAge(sec: number): string {
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  return `${Math.floor(sec / 3600)}h`;
}

